import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import {
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUpFromLine,
  Check,
  Minus,
  Package,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCreateBatchStockMovement } from '@/hooks/mutations/useStockMutations';
import { useWarehouses } from '@/hooks/queries/useWarehouses';
import { api } from '@/lib/api';
import type { Product, ProductVariant } from '@/types/product';

interface StockAdjustmentWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type MovementType = 'in' | 'out' | 'adjustment';

interface SelectedItem {
  product: Product;
  variant?: ProductVariant;
  quantity: number;
}

/** Unique key for a selected item (product or product+variant) */
function itemKey(item: SelectedItem): string {
  return item.variant?.id ? `${item.product.id}::${item.variant.id}` : item.product.id;
}

/** Display name: "Product — Variant" or just "Product" */
function itemLabel(item: SelectedItem): string {
  return item.variant ? `${item.product.name} — ${item.variant.name}` : item.product.name;
}

const TOTAL_STEPS = 4;

const MOVEMENT_TYPES: { type: MovementType; icon: typeof ArrowDownToLine; colorClass: string }[] = [
  { type: 'in', icon: ArrowDownToLine, colorClass: 'bg-lime-400' },
  { type: 'out', icon: ArrowUpFromLine, colorClass: 'bg-orange-400' },
  { type: 'adjustment', icon: SlidersHorizontal, colorClass: 'bg-blue-400' },
];

export function StockAdjustmentWizard({ open, onOpenChange }: StockAdjustmentWizardProps) {
  const { t } = useTranslation('stock');
  const { t: tc } = useTranslation('common');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [movementType, setMovementType] = useState<MovementType>('in');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('');

  const createMovement = useCreateBatchStockMovement();
  const { data: warehousesResponse } = useWarehouses();
  const warehouses = useMemo(() => warehousesResponse?.data ?? [], [warehousesResponse]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setDirection(1);
    setMovementType('in');
    setSelectedItems([]);
    setProductSearch('');
    setSearchResults([]);
    setReason('');
  }, [open]);

  useEffect(() => {
    if (!open || warehouseId || warehouses.length === 0) return;
    const def = warehouses.find((w) => w.is_default) ?? warehouses[0];
    if (def) setWarehouseId(def.id);
  }, [open, warehouseId, warehouses]);

  // Live search
  useEffect(() => {
    if (productSearch.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get<Product[]>(
          `/products?search=${encodeURIComponent(productSearch.trim())}`,
        );
        setSearchResults(res.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [productSearch]);

  const [expandedVariantProduct, setExpandedVariantProduct] = useState<Product | null>(null);

  const addItem = (product: Product, variant?: ProductVariant) => {
    const newItem: SelectedItem = { product, variant, quantity: 1 };
    const key = itemKey(newItem);
    setSelectedItems((prev) => {
      const existing = prev.find((i) => itemKey(i) === key);
      if (existing) {
        return prev.map((i) => (itemKey(i) === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, newItem];
    });
    setExpandedVariantProduct(null);
    setProductSearch('');
    setSearchResults([]);
  };

  const handleProductSelect = async (product: Product) => {
    if (product.type === 'variable') {
      // Fetch full product with variants since list API doesn't include them
      try {
        const res = await api.get<Product>(`/products/${product.id}`);
        const full = res.data ?? product;
        if (full.variants && full.variants.length > 0) {
          setExpandedVariantProduct(full);
          setSearchResults([]);
          setProductSearch('');
          return;
        }
      } catch {
        // Fallback: add as standard if fetch fails
      }
    }
    addItem(product);
  };

  const updateQuantity = (key: string, delta: number) => {
    setSelectedItems((prev) =>
      prev
        .map((i) => (itemKey(i) === key ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0),
    );
  };

  const removeItem = (key: string) => {
    setSelectedItems((prev) => prev.filter((i) => itemKey(i) !== key));
  };

  const handleClose = () => onOpenChange(false);

  const handleNext = () => {
    if (step === 1 && selectedItems.length === 0) {
      toast.error(t('quick_adjustment_product_required'));
      return;
    }
    if (step === 2) {
      if (!warehouseId) {
        toast.error(t('error_loading_warehouses'));
        return;
      }
      if (!reason.trim()) {
        toast.error(t('reason_required_adjustment'));
        return;
      }
    }
    setDirection(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const handleBack = () => {
    if (step === 0) {
      handleClose();
      return;
    }
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0 || !warehouseId || !reason.trim()) return;
    try {
      await createMovement.mutateAsync({
        type: movementType,
        warehouse_id: warehouseId,
        reason: reason.trim(),
        items: selectedItems.map((i) => ({
          product_id: i.product.id,
          product_variant_id: i.variant?.id ?? null,
          quantity: i.quantity,
        })),
      });
      toast.success(t('batch_success', { count: selectedItems.length }));
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('batch_error');
      toast.error(message);
    }
  };

  const selectedWarehouse = warehouses.find((w) => w.id === warehouseId);

  if (!open) return null;

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col pt-safe-top">
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-neutral-200 px-4 dark:border-charcoal-700">
        <Button variant="ghost" size="icon" className="size-9" onClick={handleClose}>
          <X className="size-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{t('quick_adjustment_title')}</p>
        </div>
        <span className="text-xs font-medium text-charcoal-500">
          {t('quick_adjustment_step_counter', { current: step + 1, total: TOTAL_STEPS })}
        </span>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-1.5 py-3">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={
              i === step
                ? 'h-1.5 w-7 rounded-full bg-primary-800'
                : i < step
                  ? 'h-1.5 w-2 rounded-full bg-lime-500'
                  : 'h-1.5 w-2 rounded-full bg-neutral-300'
            }
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: direction * 48 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -48 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="space-y-4"
          >
            {/* Step 0 — Movement type */}
            {step === 0 && (
              <>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{t('quick_adjustment_type_title')}</h2>
                  <p className="text-sm text-charcoal-500">
                    {t('quick_adjustment_type_description')}
                  </p>
                </div>
                <div className="space-y-3">
                  {MOVEMENT_TYPES.map(({ type, icon: Icon, colorClass }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setMovementType(type);
                        setDirection(1);
                        setStep(1);
                      }}
                      className="flex w-full items-center gap-4 rounded-md border-[length:var(--border-brutal)] border-charcoal-950 bg-white p-4 text-left shadow-[3px_3px_0_#0D0D1A] transition-all active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_#0D0D1A] dark:border-charcoal-600 dark:bg-charcoal-800 dark:shadow-[3px_3px_0_#1a1a2e]"
                    >
                      <div
                        className={cn(
                          'flex h-11 w-11 shrink-0 items-center justify-center rounded-md border-[length:var(--border-brutal-thin)] border-charcoal-950 dark:border-charcoal-600',
                          colorClass,
                        )}
                      >
                        <Icon className="h-5 w-5 text-charcoal-950" />
                      </div>
                      <div>
                        <p className="text-sm font-bold">{t(`movement_type_${type}`)}</p>
                        <p className="text-xs text-charcoal-500">
                          {t(`movement_type_${type}_desc`)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Step 1 — Select products + quantities */}
            {step === 1 && (
              <>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{t('quick_adjustment_product_title')}</h2>
                  <p className="text-sm text-charcoal-500">
                    {t('quick_adjustment_product_description')}
                  </p>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-400" />
                  <Input
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder={t('quick_adjustment_search_placeholder')}
                    className="h-12 rounded-md border-[length:var(--border-brutal)] border-neutral-300 pl-10 dark:border-charcoal-600"
                    autoFocus
                  />
                </div>

                {/* Variant expansion — when a variable product is tapped */}
                {expandedVariantProduct && (
                  <div className="space-y-2 rounded-md border-[length:var(--border-brutal)] border-charcoal-950 bg-white p-3 shadow-[2px_2px_0_#0D0D1A] dark:border-charcoal-600 dark:bg-charcoal-800 dark:shadow-none">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold">{expandedVariantProduct.name}</p>
                      <button
                        type="button"
                        onClick={() => setExpandedVariantProduct(null)}
                        className="p-1 text-charcoal-400"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-charcoal-500">
                      {t('quick_adjustment_select_variant')}
                    </p>
                    <div className="space-y-1.5">
                      {expandedVariantProduct.variants?.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => addItem(expandedVariantProduct, v)}
                          className="flex w-full items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 p-2.5 text-left active:bg-neutral-100 dark:border-charcoal-700 dark:bg-charcoal-700"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{v.name}</p>
                            <p className="text-xs text-charcoal-500">
                              {v.attributes.map((a) => `${a.attribute_name}: ${a.attribute_value}`).join(', ')}
                            </p>
                          </div>
                          <Plus className="h-4 w-4 shrink-0 text-lime-600" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search results */}
                {!expandedVariantProduct && productSearch.length >= 2 && searchResults.length > 0 && (
                  <div className="space-y-1.5">
                    {searchResults
                      .slice(0, 6)
                      .map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleProductSelect(p)}
                          className="flex w-full items-center gap-3 rounded-md border border-neutral-200 bg-white p-3 text-left transition-all active:bg-neutral-50 dark:border-charcoal-700 dark:bg-charcoal-800"
                        >
                          <Package className="h-4 w-4 shrink-0 text-charcoal-400" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{p.name}</p>
                            <p className="truncate text-xs text-charcoal-500">
                              {p.sku}
                              {p.type === 'variable' && (
                                <span className="ml-1 font-semibold text-lime-600">
                                  — Variable
                                </span>
                              )}
                            </p>
                          </div>
                          <Plus className="h-4 w-4 shrink-0 text-lime-600" />
                        </button>
                      ))}
                  </div>
                )}

                {!expandedVariantProduct && productSearch.length >= 2 && searchResults.length === 0 && !searching && (
                  <p className="py-3 text-center text-sm text-charcoal-500">
                    {t('quick_adjustment_barcode_not_found')}
                  </p>
                )}
                {searching && (
                  <p className="py-3 text-center text-sm text-charcoal-500">{tc('loading')}</p>
                )}

                {/* Selected items with quantity steppers */}
                {selectedItems.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-charcoal-500">
                      {t('quick_adjustment_selected', { count: selectedItems.length })}
                    </p>
                    {selectedItems.map((item) => (
                      <div
                        key={itemKey(item)}
                        className="flex items-center gap-3 rounded-md border-[length:var(--border-brutal)] border-charcoal-950 bg-white p-3 shadow-[2px_2px_0_#0D0D1A] dark:border-charcoal-600 dark:bg-charcoal-800 dark:shadow-none"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold">{itemLabel(item)}</p>
                          <p className="truncate text-xs text-charcoal-500">{item.variant?.sku ?? item.product.sku}</p>
                        </div>

                        {/* Quantity stepper */}
                        <div className="flex shrink-0 items-center gap-0">
                          <button
                            type="button"
                            onClick={() => updateQuantity(itemKey(item), -1)}
                            className="flex h-9 w-9 items-center justify-center rounded-l-md border-[length:var(--border-brutal-thin)] border-charcoal-950 bg-neutral-100 active:bg-neutral-200 dark:border-charcoal-600 dark:bg-charcoal-700"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <div className="flex h-9 w-10 items-center justify-center border-y-[length:var(--border-brutal-thin)] border-charcoal-950 bg-white text-sm font-bold dark:border-charcoal-600 dark:bg-charcoal-800">
                            {item.quantity}
                          </div>
                          <button
                            type="button"
                            onClick={() => updateQuantity(itemKey(item), 1)}
                            className="flex h-9 w-9 items-center justify-center rounded-r-md border-[length:var(--border-brutal-thin)] border-charcoal-950 bg-lime-400 active:bg-lime-500 dark:border-charcoal-600"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(itemKey(item))}
                          className="shrink-0 p-1 text-charcoal-400 active:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Step 2 — Warehouse + Reason */}
            {step === 2 && (
              <>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{t('quick_adjustment_details_title')}</h2>
                  <p className="text-sm text-charcoal-500">
                    {t('quick_adjustment_details_description')}
                  </p>
                </div>

                {/* Mini recap of selected items */}
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 dark:border-charcoal-700 dark:bg-charcoal-800">
                  <p className="text-xs font-bold uppercase text-charcoal-500">
                    {t(`movement_type_${movementType}`)} — {selectedItems.length}{' '}
                    {t('quick_adjustment_products_count')}
                  </p>
                  <div className="mt-1.5 space-y-0.5">
                    {selectedItems.map((item) => (
                      <p key={itemKey(item)} className="text-sm">
                        {itemLabel(item)}{' '}
                        <span className="font-bold text-lime-600">×{item.quantity}</span>
                      </p>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-neutral-700">{t('warehouse')}</label>
                  <Select value={warehouseId} onValueChange={setWarehouseId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('select_warehouse')} />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="stock_reason" className="text-sm font-medium text-neutral-700">
                    {t('reason')}
                  </label>
                  <Textarea
                    id="stock_reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('reason_required_adjustment')}
                  />
                </div>
              </>
            )}

            {/* Step 3 — Confirmation */}
            {step === 3 && (
              <>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">{t('quick_adjustment_confirm_title')}</h2>
                  <p className="text-sm text-charcoal-500">
                    {t('quick_adjustment_confirm_description')}
                  </p>
                </div>

                <div className="rounded-md border-[length:var(--border-brutal)] border-charcoal-950 bg-white p-4 shadow-[2px_2px_0_#0D0D1A] dark:border-charcoal-600 dark:bg-charcoal-800 dark:shadow-none">
                  <div className="flex items-start justify-between gap-4 border-b border-neutral-100 py-2 dark:border-charcoal-700">
                    <span className="text-sm text-charcoal-500">{t('movement_type')}</span>
                    <span className="text-sm font-bold">{t(`movement_type_${movementType}`)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 border-b border-neutral-100 py-2 dark:border-charcoal-700">
                    <span className="text-sm text-charcoal-500">{t('warehouse')}</span>
                    <span className="text-sm font-medium">{selectedWarehouse?.name ?? '-'}</span>
                  </div>
                  <div className="border-b border-neutral-100 py-2 dark:border-charcoal-700">
                    <span className="text-sm text-charcoal-500">{t('product')}</span>
                    <div className="mt-1.5 space-y-1">
                      {selectedItems.map((item) => (
                        <div
                          key={itemKey(item)}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="truncate">{itemLabel(item)}</span>
                          <span className="shrink-0 font-bold">×{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-4 pt-2">
                    <span className="text-sm text-charcoal-500">{t('reason')}</span>
                    <span className="max-w-[60%] text-right text-sm font-medium">{reason}</span>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer — hidden on step 0 */}
      {step > 0 && (
        <div className="border-t border-neutral-200 px-4 py-3 dark:border-charcoal-700">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="lg"
              className="min-w-0 flex-1 truncate"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {tc('back')}
            </Button>
            {step < TOTAL_STEPS - 1 ? (
              <Button
                variant="cta"
                size="lg"
                className="min-w-0 flex-1 truncate"
                onClick={handleNext}
                disabled={step === 1 && selectedItems.length === 0}
              >
                {tc('next')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="cta"
                size="lg"
                className="min-w-0 flex-1 truncate"
                onClick={handleSubmit}
                loading={createMovement.isPending}
              >
                <Check className="mr-2 h-4 w-4" />
                {t('validate_adjustment')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
